// Pending resolution proposals, shown ON the issue's resolve page.
//
// Why this exists: a gated submission (from the web dialog or from the
// Revit / Navisworks plugin) parks its proof-of-fix on the PROPOSAL and
// leaves the issue at `under_review` - the evidence is copied onto
// `issue.resolution.evidence` only when a reviewer approves. The resolve
// page used to render evidence solely for `status === "resolved"`, so a
// reviewer arriving from the "please review" email saw the issue with no
// screenshot and no element list: exactly the state they were asked to
// judge was the one state that showed nothing.
import React, { useCallback, useEffect, useState } from "react";
import { Avatar, Button, Card, Input, Tag, Tooltip, message } from "antd";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";
import { url, tokenRef } from "api";
import { T } from "pages/Issue/theme";
import { EvidenceList } from "components/Issue/IssueSourceControls";
import { proposalEvidenceItems } from "helpers/proposalEvidence";

const CHANNEL_LABEL = { revit: "Revit", navisworks: "Navisworks", web: "Web" };

function authHeader() {
  return { Authorization: `Bearer ${localStorage.getItem(tokenRef)}` };
}

export default function PendingProposalsPanel({ issueId, canReview = false, onReviewed }) {
  const [proposals, setProposals] = useState([]);
  const [note, setNote] = useState("");
  const [working, setWorking] = useState("");

  const load = useCallback(async () => {
    if (!issueId) return;
    try {
      const res = await fetch(`${url}/issue/${issueId}/proposals`, { headers: authHeader() });
      // 403 = this viewer may not read proposals (e.g. a partner employee):
      // stay silent rather than shouting an error onto the page.
      if (!res.ok) { setProposals([]); return; }
      const body = await res.json();
      setProposals((Array.isArray(body) ? body : []).filter((p) => p.status === "pending"));
    } catch (e) {
      setProposals([]);
    }
  }, [issueId]);

  useEffect(() => { load(); }, [load]);

  const decide = async (id, action) => {
    setWorking(id + action);
    try {
      const res = await fetch(`${url}/issue/proposals/${id}/${action}`, {
        method: "POST",
        headers: { ...authHeader(), "Content-Type": "application/json" },
        body: JSON.stringify({ note }),
      });
      if (!res.ok) throw new Error((await res.json())?.err || `HTTP ${res.status}`);
      message.success(action === "approve" ? "Resolution approved" : "Sent back to the contributor");
      setNote("");
      await load();
      if (onReviewed) await onReviewed();
    } catch (e) {
      message.error(e.message || `Could not ${action} the resolution`);
    } finally {
      setWorking("");
    }
  };

  if (!proposals.length) return null;

  return (
    <div style={{ marginBottom: 10 }}>
      {proposals.map((p) => {
        const u = p.proposerId || {};
        const who = `${u.name || ""} ${u.lastName || ""}`.trim() || u.email || "Contributor";
        const items = proposalEvidenceItems(p);
        const els = p.resolvedElements || [];
        const channel = CHANNEL_LABEL[p.submissionChannel] || p.submissionChannel || "Web";
        return (
          <Card
            key={p._id}
            size="small"
            style={{ marginBottom: 8, borderColor: "#F5B14C", background: "#FFFBF3" }}
          >
            <div style={{ display: "flex", alignItems: "center", marginBottom: 6, gap: 8 }}>
              <Avatar size="small" style={{ background: T.navy }}>{who.charAt(0).toUpperCase()}</Avatar>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12.5, fontWeight: 600, color: T.ink }}>
                  {who} proposed a resolution
                </div>
                <div style={{ fontSize: 11, color: T.muted }}>
                  {new Date(p.submittedAt).toLocaleString()} · submitted from {channel}
                </div>
              </div>
              <Tag color="orange" style={{ marginRight: 0 }}>AWAITING REVIEW</Tag>
            </div>

            {p.notes ? (
              <div style={{ fontSize: 12.5, color: T.text, fontStyle: "italic", marginBottom: 6 }}>
                "{p.notes}"
              </div>
            ) : null}

            {items.length ? (
              <EvidenceList compact title="PROPOSED RESOLUTION EVIDENCE" items={items} />
            ) : (
              <div style={{ fontSize: 11.5, color: T.muted }}>
                No screenshot or document was attached to this submission.
              </div>
            )}

            {els.length ? (
              <div style={{ fontSize: 11.5, color: T.muted, marginTop: 6 }}>
                {els.length} model element{els.length === 1 ? "" : "s"} reported fixed
                {p.commitId ? " · linked to a model commit" : ""}
                <div style={{ marginTop: 3, fontFamily: "monospace", fontSize: 10.5 }}>
                  {els.slice(0, 5).map((e, i) => (
                    <div key={`${e.uniqueId}-${i}`}>
                      · {e.uniqueId}
                      {e.fragmentHash ? ` · ${String(e.fragmentHash).slice(0, 8)}…` : ""}
                    </div>
                  ))}
                  {els.length > 5 ? <div>+{els.length - 5} more</div> : null}
                </div>
              </div>
            ) : null}

            {canReview ? (
              <div style={{ marginTop: 8 }}>
                <Input.TextArea
                  rows={2}
                  placeholder="Review note (optional)"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  style={{ marginBottom: 6, fontSize: 12 }}
                />
                <div style={{ display: "flex", gap: 8 }}>
                  <Button
                    type="primary" size="small" icon={<CheckOutlined />} block
                    loading={working === p._id + "approve"}
                    onClick={() => decide(p._id, "approve")}
                  >
                    Approve resolution
                  </Button>
                  <Tooltip title="Send back to the contributor">
                    <Button
                      danger size="small" icon={<CloseOutlined />} block
                      loading={working === p._id + "reject"}
                      onClick={() => decide(p._id, "reject")}
                    >
                      Reject
                    </Button>
                  </Tooltip>
                </div>
              </div>
            ) : null}
          </Card>
        );
      })}
    </div>
  );
}
